// A simple repeatable photo field backed by the same live in-page camera as
// GuidedVehicleDamagePhotos -- no guided walkthrough or outline overlay,
// just a reliable "take as many photos as you need" field for categories
// that aren't a fixed set of vehicle views (the scene, another vehicle's
// damage). Replaces the plain <input capture> hand-off to the native
// camera app, which can silently fail to return a photo at all when DMS is
// installed to an iOS home screen -- see GuidedVehicleDamagePhotos.tsx.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLiveCameraCapture } from "@/lib/useLiveCameraCapture";
import { uploadIncidentPhotoBlob, removeIncidentPhoto, IncidentPhotoRef } from "@/lib/incidentPhotoUpload";
import { Camera, Check, Loader2, AlertTriangle, Trash2, X as XIcon } from "lucide-react";

export type FieldPhoto = IncidentPhotoRef;

interface LiveCameraPhotoFieldProps {
  incidentId: string;
  category: string;
  photos: FieldPhoto[];
  location: { lat: number; lng: number } | null;
  uploadedBy: string;
  onPhotosChanged: (photos: FieldPhoto[]) => void;
}

const LiveCameraPhotoField: React.FC<LiveCameraPhotoFieldProps> = ({
  incidentId,
  category,
  photos,
  location,
  uploadedBy,
  onPhotosChanged,
}) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const camera = useLiveCameraCapture();

  useEffect(() => {
    if (open) void camera.start();
    else camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPhoto = (photo: FieldPhoto) => onPhotosChanged([...photos, photo]);

  const captureFromVideo = async () => {
    setUploading(true);
    camera.setError("");
    try {
      const blob = await camera.captureFrame();
      if (!blob) throw new Error(camera.error || "Capture failed -- try again.");
      const photo = await uploadIncidentPhotoBlob({ incidentId, category, blob, filenameHint: category, location, uploadedBy });
      addPhoto(photo);
    } catch (e) {
      camera.setError(e instanceof Error ? e.message : "Capture failed -- try again.");
    } finally {
      setUploading(false);
    }
  };

  const captureFromFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    camera.setError("");
    try {
      const photo = await uploadIncidentPhotoBlob({ incidentId, category, blob: file, filenameHint: category, location, uploadedBy });
      addPhoto(photo);
    } catch (e) {
      camera.setError(e instanceof Error ? e.message : "Upload failed -- try again.");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photo: FieldPhoto) => {
    try {
      await removeIncidentPhoto(photo);
      onPhotosChanged(photos.filter((p) => p.id !== photo.id));
    } catch {
      // Best-effort -- leave it in the list rather than hide a photo that's still saved.
    }
  };

  if (!open) {
    return (
      <div className="flex flex-wrap gap-2">
        {photos.map((photo) => (
          <div key={photo.id} className="relative w-20 h-20 rounded-md overflow-hidden border border-border">
            <img src={photo.url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removePhoto(photo)}
              className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-20 h-20 rounded-md border border-dashed border-border flex items-center justify-center hover:border-yellow-500/60"
        >
          <Camera className="w-6 h-6 text-muted-foreground" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative w-full aspect-[4/3] overflow-hidden rounded-xl bg-black border border-border">
        <video
          ref={camera.videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: camera.ready ? 1 : 0 }}
        />
        {!camera.ready && !camera.fallback && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Starting camera...
          </div>
        )}
      </div>

      {camera.error && (
        <div className="flex items-start gap-1.5 text-xs text-amber-400">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{camera.error}</span>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {camera.fallback ? (
          <label className="inline-flex">
            <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
              <span>
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
                Use Phone Camera
              </span>
            </Button>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                void captureFromFile(e.target.files?.[0] || null);
                e.target.value = "";
              }}
            />
          </label>
        ) : (
          <Button type="button" size="sm" disabled={!camera.ready || uploading} onClick={captureFromVideo} className="golden-button">
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
            {uploading ? "Saving..." : "Capture"}
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
          <Check className="w-4 h-4 mr-1.5" />
          Done
        </Button>
      </div>

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-border">
              <img src={photo.url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(photo)}
                className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveCameraPhotoField;
